import { inject, Injectable } from '@angular/core';
import { GeneralActions } from '@hunting-nature/Secacam/shared/redux/general/general.actions';
import { NavController } from '@ionic/angular/standalone';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { ContactsRestService } from '../../../../shared/services/contacts/contacts.rest.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { CamerasActions } from '../../redux/cameras.actions';

import { CameraContactsActions } from './camera-contacts.actions';
import { CameraContactsFlowService } from './camera-contacts.flow.service';
import type { CameraContactsFlowRequest } from './camera-contacts.flow.service';
import type { CameraContactsSubmitFailure } from './camera-contacts.types';

@Injectable()
export class CameraContactsEffects {
  private readonly actions$ = inject(Actions);
  private readonly navController = inject(NavController);
  private readonly toastService = inject(ToastService);
  private readonly contactFlowService = inject(CameraContactsFlowService);
  private readonly contactsRestService = inject(ContactsRestService);

  // goTo

  goToInviteNewContact$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CameraContactsActions.goToInviteNewContact),
        switchMap(action => this.navController.navigateForward(['home/cameras', action.cameraId, 'contacts', 'invite-new']))
      ),
    { dispatch: false }
  );

  goToInviteContactList$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CameraContactsActions.goToInviteContactList),
        switchMap(action => this.navController.navigateForward(['home/cameras', action.cameraId, 'contacts', 'invite']))
      ),
    { dispatch: false }
  );

  goToCameraContactsList$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CameraContactsActions.goToCameraContactsList),
        switchMap(action => this.navController.navigateForward(['home/cameras', action.cameraId, 'contacts']))
      ),
    { dispatch: false }
  );

  goToInviteNewContactPermissions$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CameraContactsActions.goToInviteNewContactPermissions),
        switchMap(action =>
          this.navController.navigateForward(['home/cameras', action.cameraId, 'contacts', 'invite-new', 'permissions'], {
            state: {
              name: action.contactName,
              email: action.contactEmail,
            },
          })
        )
      ),
    { dispatch: false }
  );

  // goBack

  goBackToCameraDetails$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CameraContactsActions.goBackToCameraDetailsFromContactsList, CameraContactsActions.goBackToCameraDetailsFromInviteNewContact),
        switchMap(action => this.navController.navigateBack(['home/cameras', action.cameraId]))
      ),
    { dispatch: false }
  );

  goBackToContacts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CameraContactsActions.goBackToContactsFromInviteList, CameraContactsActions.submitCameraContactSucceeded),
        switchMap(action => this.navController.navigateBack(['home/cameras', action.cameraId, 'contacts']))
      ),
    { dispatch: false }
  );

  goBackToInviteList$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CameraContactsActions.goBackToInviteListFromInviteNewContact),
        switchMap(action => this.navController.navigateBack(['home/cameras', action.cameraId, 'contacts', 'invite']))
      ),
    { dispatch: false }
  );

  goBackToInviteNewContact$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CameraContactsActions.goBackToInviteNewContactFromPermissions),
        switchMap(action =>
          this.navController.navigateBack(['home/cameras', action.cameraId, 'contacts', 'invite-new'], {
            state: {
              name: action.contactName,
              email: action.contactEmail,
            },
          })
        )
      ),
    { dispatch: false }
  );

  // entered

  loadContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CameraContactsActions.contactsListEntered,
        CameraContactsActions.inviteContactListEntered,
        CamerasActions.cameraShareInviteSentBackToContactsClicked
      ),
      switchMap(() =>
        this.contactsRestService.fetchAllContacts$().pipe(
          map(contacts => CameraContactsActions.loadContactsSucceeded({ contacts })),
          catchError((error: unknown) => of(CameraContactsActions.loadContactsFailed({ error })))
        )
      )
    )
  );

  // business logic

  confirmRemoveCameraContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CameraContactsActions.confirmRemoveCameraContact),
      switchMap(({ contactId, cameraId }) =>
        this.contactsRestService.removeCameraFromContact$(contactId, cameraId).pipe(
          switchMap(() =>
            this.contactsRestService.fetchAllContacts$().pipe(
              map(contacts => CameraContactsActions.confirmRemoveCameraContactSucceeded({ cameraId, contacts })),
              catchError((error: unknown) => of(CameraContactsActions.confirmRemoveCameraContactFailed({ error })))
            )
          ),
          catchError((error: unknown) => of(CameraContactsActions.confirmRemoveCameraContactFailed({ error })))
        )
      )
    )
  );

  confirmRemoveCameraContactFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CameraContactsActions.confirmRemoveCameraContactFailed),
        tap(() => this.toastService.showTranslatedError('menu.contacts.newContactErrors.removeContactFailed'))
      ),
    { dispatch: false }
  );

  submitCameraContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CameraContactsActions.submitCameraContact),
      switchMap(action => {
        const trimmedName = action.contactName.trim();
        const trimmedEmail = action.contactEmail.trim();
        const failureReason = this.getValidationFailure(trimmedName, trimmedEmail);

        if (failureReason) {
          return of(CameraContactsActions.submitCameraContactFailed({ reason: failureReason }));
        }

        const flowRequest: CameraContactsFlowRequest = {
          cameraId: action.cameraId,
          contactName: trimmedName,
          contactEmail: trimmedEmail,
          permission: action.permission,
        };

        return this.contactFlowService.shareCameraContact$(flowRequest).pipe(
          switchMap(() =>
            this.contactsRestService.fetchAllContacts$().pipe(
              map(contacts => CameraContactsActions.submitCameraContactSucceeded({ cameraId: action.cameraId, contacts })),
              catchError(() => of(CameraContactsActions.submitCameraContactFailed({ reason: 'REQUEST_FAILED' })))
            )
          ),
          catchError(() => of(CameraContactsActions.submitCameraContactFailed({ reason: 'REQUEST_FAILED' })))
        );
      })
    )
  );

  submitCameraContactFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CameraContactsActions.submitCameraContactFailed),
        tap(({ reason }) => this.toastService.showTranslatedError(this.getSubmitErrorKey(reason)))
      ),
    { dispatch: false }
  );

  updateContactPermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CameraContactsActions.updateContactPermission),
      switchMap(({ contactId, cameraId, permission, previousPermission }) =>
        this.contactsRestService.updateCameraContactPermission$(contactId, cameraId, permission).pipe(
          switchMap(() =>
            this.contactsRestService.fetchAllContacts$().pipe(
              map(contacts => CameraContactsActions.updateContactPermissionSucceeded({ cameraId, contacts })),
              catchError((error: unknown) => of(CameraContactsActions.updateContactPermissionFailed({ error, cameraId, previousPermission })))
            )
          ),
          catchError((error: unknown) => of(CameraContactsActions.updateContactPermissionFailed({ error, cameraId, previousPermission })))
        )
      )
    )
  );

  updateContactPermissionFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CameraContactsActions.updateContactPermissionFailed),
        tap(() => this.toastService.showTranslatedError('menu.contacts.newContactErrors.updatePermissionFailed'))
      ),
    { dispatch: false }
  );

  selectContactForCameraShare$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CameraContactsActions.selectContactForCameraShare),
      map(() => CamerasActions.openCameraShareContactSheet())
    )
  );

  viewCameraContactProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CameraContactsActions.viewCameraContactProfile),
      map(() => GeneralActions.openComingSoonModal())
    )
  );

  private getValidationFailure(name: string, email: string): CameraContactsSubmitFailure | undefined {
    if (!name) {
      return 'NAME_EMPTY';
    }

    if (!this.isValidEmail(email)) {
      return 'EMAIL_INVALID';
    }

    return undefined;
  }

  private isValidEmail(email: string): boolean {
    const [localPart, domain] = email.split('@');
    if (!localPart?.length || !domain?.length) {
      return false;
    }

    return domain.includes('.') && !/\s/.test(localPart) && !/\s/.test(domain);
  }

  private getSubmitErrorKey(reason: CameraContactsSubmitFailure): string {
    switch (reason) {
      case 'NAME_EMPTY':
        return 'menu.contacts.newContactErrors.nameRequired';
      case 'EMAIL_INVALID':
        return 'menu.contacts.newContactErrors.emailInvalid';
      default:
        return 'menu.contacts.newContactErrors.requestFailed';
    }
  }
}
